// ============================================================================
// TAK Destinations (Phase 4 item 24) — Settings UI for multi-server TAK
// outbound configuration. Live tracks are streamed to every enabled
// destination as CoT XML by the ingestion worker.
//
// What this screen does:
//   - Lists the tenant's configured TAK servers with live health (last
//     connected time + last error).
//   - Lets operators add, edit, delete destinations, and fire a
//     connectivity probe without leaving the page.
//   - Accepts PEM-formatted client cert, private key, and truststore as
//     pasted text. We never re-display the private key after save — the
//     API redacts it and the UI shows "Key on file / No key on file".
// ============================================================================
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  SignalIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  takServersApi,
  type TakServer,
  type TakTransport,
  type CreateTakServerInput,
} from '../../services/takServersApi';
import ConfirmModal from '../common/ConfirmModal';

// ---------------------------------------------------------------------------
// Status pill — green / amber / red based on health columns.
// ---------------------------------------------------------------------------

function StatusPill({ server }: { server: TakServer }) {
  const hasRecentError =
    !!server.lastError &&
    !!server.lastErrorAt &&
    (!server.lastConnectedAt ||
      new Date(server.lastErrorAt) > new Date(server.lastConnectedAt));

  if (!server.enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-700/40 text-gray-300">
        Disabled
      </span>
    );
  }
  if (hasRecentError) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/10 text-red-400">
        <ExclamationTriangleIcon className="h-3 w-3" />
        Error
      </span>
    );
  }
  if (server.lastConnectedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-400">
        <CheckCircleIcon className="h-3 w-3" />
        Connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400">
      Pending
    </span>
  );
}

// ---------------------------------------------------------------------------
// Add / edit modal.
// ---------------------------------------------------------------------------

interface FormValues {
  label: string;
  host: string;
  port: number;
  transport: TakTransport;
  enabled: boolean;
  clientCertPem: string;
  clientKeyPem: string;
  truststorePem: string;
}

function DestinationModal({
  server,
  onClose,
  onSaved,
}: {
  server: TakServer | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!server;
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } =
    useForm<FormValues>({
      defaultValues: {
        label: server?.label ?? '',
        host: server?.host ?? '',
        port: server?.port ?? 8089,
        transport: server?.transport ?? 'TLS',
        enabled: server?.enabled ?? true,
        clientCertPem: '',
        clientKeyPem: '',
        truststorePem: '',
      },
    });

  const transport = watch('transport');

  const onSubmit = async (values: FormValues) => {
    // Collapse empty PEM fields so we don't POST empty strings — the zod
    // refine on the server requires the field to look like a PEM block
    // when present.
    const payload: CreateTakServerInput = {
      label: values.label.trim(),
      host: values.host.trim(),
      port: Number(values.port),
      transport: values.transport,
      enabled: values.enabled,
    };
    if (values.clientCertPem.trim()) payload.clientCertPem = values.clientCertPem.trim();
    if (values.clientKeyPem.trim()) payload.clientKeyPem = values.clientKeyPem.trim();
    if (values.truststorePem.trim()) payload.truststorePem = values.truststorePem.trim();

    try {
      if (isEdit && server) {
        await takServersApi.update(server.id, payload);
        toast.success('TAK destination updated');
      } else {
        await takServersApi.create(payload);
        toast.success('TAK destination created');
      }
      onSaved();
      onClose();
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Save failed';
      toast.error(message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-navy-900 border border-navy-700 rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
          <h3 className="text-lg font-semibold text-white">
            {isEdit ? 'Edit TAK destination' : 'Add TAK destination'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Label</label>
              <input
                {...register('label', { required: 'Required', maxLength: 100 })}
                placeholder="HQ TAK, Field Ops…"
                className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
              />
              {errors.label && (
                <p className="text-[11px] text-red-400 mt-1">{errors.label.message}</p>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Host</label>
              <input
                {...register('host', { required: 'Required' })}
                placeholder="tak.example.com"
                className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
              />
              {errors.host && (
                <p className="text-[11px] text-red-400 mt-1">{errors.host.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Port</label>
              <input
                type="number"
                {...register('port', { required: true, valueAsNumber: true, min: 1, max: 65535 })}
                className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Transport</label>
              <select
                {...register('transport')}
                className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
              >
                <option value="TLS">TLS (recommended)</option>
                <option value="TCP">TCP</option>
                <option value="UDP">UDP</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300 select-none">
            <input
              type="checkbox"
              {...register('enabled')}
              className="rounded border-navy-600 bg-navy-800 text-accent focus:ring-accent/30"
            />
            Enabled — the ingestion worker will maintain a live connection.
          </label>

          {/* TLS material — only meaningful for TLS. We still allow pasting
              on TCP because operators occasionally pre-load material before
              switching transport, but we label the section clearly. */}
          <div className={clsx('space-y-3', transport !== 'TLS' && 'opacity-70')}>
            <div className="border-t border-navy-700 pt-4">
              <h4 className="text-sm font-semibold text-white mb-1">TLS material</h4>
              <p className="text-[11px] text-gray-500 mb-3">
                {transport === 'TLS'
                  ? 'Paste PEM-formatted blocks. The private key is encrypted at rest and never returned from the API.'
                  : 'Only used when transport is TLS.'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Client certificate (PEM){' '}
                {isEdit && server?.hasClientCert && (
                  <span className="text-[10px] text-emerald-400 ml-1">· on file</span>
                )}
              </label>
              <textarea
                {...register('clientCertPem')}
                rows={4}
                placeholder={isEdit && server?.hasClientCert
                  ? 'Leave blank to keep existing cert'
                  : '-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Client private key (PEM){' '}
                {isEdit && server?.hasClientKey && (
                  <span className="text-[10px] text-emerald-400 ml-1">· encrypted, on file</span>
                )}
              </label>
              <textarea
                {...register('clientKeyPem')}
                rows={4}
                placeholder={isEdit && server?.hasClientKey
                  ? 'Leave blank to keep existing key'
                  : '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
                className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Truststore / server CA (PEM){' '}
                {isEdit && server?.hasTruststore && (
                  <span className="text-[10px] text-emerald-400 ml-1">· on file</span>
                )}
              </label>
              <textarea
                {...register('truststorePem')}
                rows={4}
                placeholder={isEdit && server?.hasTruststore
                  ? 'Leave blank to keep existing truststore'
                  : 'Paste the CA chain the TAK server is signed by'}
                className="w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                If omitted, the worker still connects but does not verify the server
                certificate — fine for self-signed internal deployments, unsafe over
                the public internet.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-navy-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary px-5 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create destination'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main section.
// ---------------------------------------------------------------------------

export default function TakDestinationsSection() {
  const qc = useQueryClient();
  const { data: servers = [], isLoading } = useQuery<TakServer[]>({
    queryKey: ['tak-servers'],
    queryFn: () => takServersApi.list(),
    // Refresh every 10s so the health pills don't go stale while the user
    // watches the tab.
    refetchInterval: 10_000,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TakServer | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TakServer | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => takServersApi.remove(id),
    onSuccess: () => {
      toast.success('TAK destination deleted');
      qc.invalidateQueries({ queryKey: ['tak-servers'] });
    },
    onError: () => toast.error('Delete failed'),
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => takServersApi.test(id),
    onSuccess: (res, id) => {
      const server = servers.find((s) => s.id === id);
      const label = server?.label ?? 'destination';
      if (res.ok) {
        toast.success(
          `${label}: reachable${res.latencyMs !== null ? ` (${res.latencyMs} ms)` : ''}`,
        );
      } else {
        toast.error(`${label}: ${res.error ?? 'unreachable'}`);
      }
      qc.invalidateQueries({ queryKey: ['tak-servers'] });
    },
    onError: () => toast.error('Test failed'),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (s: TakServer) => {
    setEditing(s);
    setModalOpen(true);
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">TAK Destinations</h3>
          <p className="text-sm text-gray-500">
            Stream live track updates as CoT XML to one or more TAK servers.
            Changes take effect without restarting ingestion.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add destination
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading destinations…</p>
      ) : servers.length === 0 ? (
        <div className="border border-dashed border-navy-700 rounded-lg py-10 text-center">
          <p className="text-sm text-gray-400">No TAK destinations yet.</p>
          <p className="text-[12px] text-gray-600 mt-1">
            Add one to start streaming live tracks.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-navy-700/60">
          {servers.map((s) => (
            <li key={s.id} className="py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white truncate">{s.label}</p>
                  <StatusPill server={s} />
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">
                    {s.transport}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {s.host}:{s.port}
                  {s.hasClientCert ? ' · client cert' : ''}
                  {s.hasClientKey ? ' · key' : ''}
                  {s.hasTruststore ? ' · truststore' : ''}
                </p>
                {s.lastError && (
                  <p className="text-[11px] text-red-400 mt-0.5 line-clamp-1">
                    {s.lastError}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => testMutation.mutate(s.id)}
                  disabled={testMutation.isPending}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-300 hover:text-white bg-navy-800/60 border border-navy-700 rounded transition-colors disabled:opacity-40"
                  title="Run a connectivity probe"
                >
                  <SignalIcon className="h-3.5 w-3.5" />
                  Test
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(s)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-300 hover:text-white bg-navy-800/60 border border-navy-700 rounded transition-colors"
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(s)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-red-400 hover:text-red-300 bg-navy-800/60 border border-navy-700 rounded transition-colors"
                  aria-label={`Delete ${s.label}`}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {modalOpen && (
        <DestinationModal
          server={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['tak-servers'] })}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete TAK destination?"
        message={
          confirmDelete
            ? `"${confirmDelete.label}" will stop receiving live tracks. You can re-add it later.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete.id);
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
}
